import { AsyncPipe, NgIf } from '@angular/common';
import { Component, DestroyRef, forwardRef, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, map, startWith, tap } from 'rxjs';

export type OptionItemType<T> = {
  id?: string | number;
  name: string;
} & T;

@Component({
  selector: 'app-searchable-filter-dropdown',
  templateUrl: './searchable-filter-dropdown.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableFilterDropdownComponent),
      multi: true,
    },
  ],
  styles: `
    ::ng-deep .mat-mdc-form-field-subscript-wrapper {
      display: none !important;
    }

    ::ng-deep .mat-mdc-select-value-text {
      padding-left: 8px !important;
    }
  `,
  imports: [MatFormFieldModule, MatSelectModule, ReactiveFormsModule, TranslateModule, AsyncPipe, NgIf, MatInputModule],
})
export class SearchableFilterDropdownComponent<T> implements ControlValueAccessor, OnInit, OnChanges {
  @Input() public options: OptionItemType<T>[] = [];
  @Input() public label = '';
  @Input() public showSearch = true;
  @Input() public filterPlaceholder = 'global.filter.searchPlaceholder';
  @Input() public multiple = true;
  public value: OptionItemType<T>[] | null = null;
  public filterControl = new FormControl();
  public selectionControl = new FormControl<OptionItemType<T>[]>([]);
  public filteredOptions$ = new BehaviorSubject<OptionItemType<T>[]>([]);
  public showClearButton$ = this.selectionControl.valueChanges.pipe(
    map((value) => {
      if (this.multiple) {
        return this.multiple && value?.length;
      }
      return Array.isArray(value) ? value.length : Boolean(value);
    })
  );
  private readonly destroyRef = inject(DestroyRef);

  public ngOnInit(): void {
    this.filterControl.valueChanges
      .pipe(
        startWith(''),
        map((query) => {
          if (query) {
            return this.options.filter((option) => option.name.toLowerCase().includes(query.toLowerCase()));
          }
          return this.options;
        }),
        tap((options) => {
          this.filteredOptions$.next(options);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const newOptions: OptionItemType<T>[] = changes['options'].currentValue;
    if (newOptions) {
      const query = this.filterControl.value;
      if (query) {
        const filteredOptions = newOptions.filter((option) => {
          const searchItem = option.name ?? option;
          return searchItem.toLowerCase().includes(query);
        });
        this.filteredOptions$.next(filteredOptions);
      }
      this.filteredOptions$.next(newOptions);
    }
  }

  public writeValue(value: OptionItemType<T>[]): void {
    this.value = value;
    this.selectionControl.setValue(value);
  }

  public registerOnChange(onChange: (value: OptionItemType<T>[]) => void): void {
    this.onChange = onChange;
  }

  public registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  public onChange = (value: OptionItemType<T>[]): void => {
    //placeholder implementation
    console.log(`Value changed ${value}`);
  };

  public onTouched = (): void => {
    //placeholder implementation
    console.log(`Form control ${SearchableFilterDropdownComponent.name} touched`);
  };

  public updateSelected(value: OptionItemType<T>[]): void {
    this.writeValue(value);
    this.onChange(value);
    this.selectionControl.setValue(value);
  }

  public onSelectionChange(event: MatSelectChange): void {
    this.updateSelected(event.value);
  }

  public clearInput(event: MouseEvent): void {
    event.stopPropagation();
    this.updateSelected([]);
  }

  public compareFn(a: OptionItemType<T>, b: OptionItemType<T>): boolean {
    const hasOptions = a && b;
    if (!hasOptions) return false;

    const hasId = Boolean(a.id) && Boolean(b.id);

    if (!hasId) {
      return a.name === b.name;
    }

    return hasOptions && hasId ? a.id === b.id : false;
  }

  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.filterControl.disable();
      this.selectionControl.disable();
    } else {
      this.filterControl.enable();
      this.selectionControl.enable();
    }
  }

  public keyDownHandler(event: KeyboardEvent): void {
    if (event.code === 'Space') {
      event.stopPropagation();
    }
  }
}
